/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useEffect, useState } from "react";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import SelectImageFormat from "cms/images/image-inpage-editor-v2/select-image-format";
import ScaleMethod from "cms/images/image-inpage-editor-v2/scale-methods";
import SetImageDimension from "cms/images/image-inpage-editor-v2/set-image-dimension";
import FocusAreaPosition from "cms/images/image-inpage-editor-v2/focus-area";
import ToggleUseCaption from "cms/images/image-inpage-editor-v2/toggle-use-caption";
import Config from "data/config";
import { isStringNotEmpty, exists, isObject, isTrue, isFalse, isValidGuid } from "utils/empty-exists";
import { IMAGE } from "cms/images/image-inpage-editor-v2/image-helpers";
import Typography from "@mui/material/Typography";
import { ImageSelector } from "cms/images/image-selector";
import { Logger } from "cms/utils/log/logger";

const ImageInPageEditorV2 = (props) => {

    const enableImageDebug = Config.Debug.enableDiagnostics;

    const {
        image_guid,
        settings: _settings,
        onChangeImageWithSettings,
        onRemoveImage,
        disableScale = false,
        AddButtonComponent,
        DisplayComponent
    } = props || {};

    const { token } = useAuth();
    const [image, setImage] = useState();

    const initialSettings = isObject(_settings)
        ? _settings
        : IMAGE.DEFAULTS.DEFAULT_IMAGE_SETTINGS;
    const { width, height } = image ||{};
    const initialFormat = initialSettings?.format;
    const initialPosition = initialSettings?.options?.position
        ? initialSettings?.options?.position
        : IMAGE.DEFAULTS.DEFAULT_POSITION;
    const initialMethod = initialSettings?.options?.method;
    const initialDimensions = initialSettings?.options?.dimension
        ? initialSettings?.options?.dimension
        : IMAGE.DEFAULTS.DEFAULT_SIZE;
    const isCropped = initialFormat === IMAGE.FORMATS.CROPPED_FOCUS.value;
    const isScaled = initialFormat === IMAGE.FORMATS.SCALED_IMAGE.value;
    const defaultWidth = width ? width : IMAGE.DEFAULTS.DEFAULT_SIZE;
    const defaultHeight = height ? height : IMAGE.DEFAULTS.DEFAULT_SIZE;

    const [imageLoading, setImageLoading] = useState(true);
    const [settings, setSettings] = useState(initialSettings);
    const [imageWidth, setImageObjWidth] = useState(width);
    const [imageHeight, setImageObjHeight] = useState(height);
    const [showPosition, setShowPosition] = useState(isCropped);
    const [showScaled, setShowScaled] = useState(isScaled);
    const [formatName, setFormatName] = useState(IMAGE.FORMATS.CROPPED_FOCUS.name);
    const [selectedScaleMethod, setSelectedScaleMethod] = useState(initialMethod);
    const [selectedFormat, setSelectedFormat] = useState(initialFormat);
    const [selectedPosition, setSelectedPosition] = useState(initialPosition);

    const isHeightPriority = selectedScaleMethod === IMAGE.METHODS.HEIGHT_PRIORITY;
    const widthOrHeight = isHeightPriority ? defaultHeight : defaultWidth;

    const [dimension, setDimension] = useState(initialDimensions);
    const [whichDimension, setWhichDimension] = useState(IMAGE.DIMENSIONS.DIM_HEIGHT);

    useEffect(() => {
        setDimension(_settings?.options?.dimension);
    }, [_settings?.options?.dimension]);

    const fetchImage = async (guid) => {
        // const hasChanged = guid !== image?.guid
        // const wasLoaded = !!image?.guid
        // if (isValidGuid(guid) && hasChanged) {
        if (isValidGuid(guid)) {
            setImageLoading(true);
            let currentImage = await SimpliCityAdminSDK.images.findOne(token, guid);
            setImage(currentImage);
            const { width, height } = currentImage || {};
            setImageObjWidth(width);
            setImageObjHeight(height);
            setImageLoading(false);
        } else {
            setImageLoading(false);
            setImage(null);
        }
    };

    useEffect(() => {
        if (isStringNotEmpty(image_guid)) {
            fetchImage(image_guid);
        } else {
            setImageLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [image_guid]);

    useEffect(() => {
        if (selectedScaleMethod === IMAGE.METHODS.HEIGHT_PRIORITY) {
            setWhichDimension(IMAGE.DIMENSIONS.DIM_HEIGHT);
            if (imageHeight) {
                const dim = imageHeight || IMAGE.DEFAULTS.DEFAULT_SIZE;
                setDimension(dim);
            }
        } else {
            setWhichDimension(IMAGE.DIMENSIONS.DIM_WIDTH);
            const dim = imageWidth || IMAGE.DEFAULTS.DEFAULT_SIZE;
            setDimension(dim);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedScaleMethod]);

    const selectPositionType = (event, position) => {
        setSelectedPosition(position);
        const nextSettings = structuredClone(settings);
        nextSettings.options.position = position;
        setSettings(nextSettings);
        onChangeImageWithSettings(image,nextSettings);
    };
    const clear = () => {
        setImage(undefined);
        setImageObjWidth(IMAGE.DEFAULTS.DEFAULT_SIZE);
        setImageObjHeight(IMAGE.DEFAULTS.DEFAULT_SIZE);
        setSettings(IMAGE.DEFAULTS.DEFAULT_IMAGE_SETTINGS);
        setShowScaled(true);
    };
    // called when Cropped or Scaled toggle button is used
    const onChooseFormat = (event, format) => {
        if (format === null) {
            return;
        }
        setSelectedFormat(format);
        const useCaption = settings?.options?.useCaption;
        if (format === IMAGE.FORMATS.CROPPED_FOCUS.value) {
            setShowPosition(true);
            setShowScaled(false);
            setFormatName(IMAGE.FORMATS.CROPPED_FOCUS.name);
            setSelectedPosition(IMAGE.DEFAULTS.DEFAULT_POSITION);
            const nextSettings = {
                format: IMAGE.FORMATS.CROPPED_FOCUS.value,
                options: {
                    position: IMAGE.DEFAULTS.DEFAULT_POSITION,
                    method: null,
                    dimension: widthOrHeight,
                    useCaption,
                }
            };
            setSettings(nextSettings);
            onChangeImageWithSettings(image,nextSettings);
        } else {
            setShowPosition(false);
            setShowScaled(true);
            setFormatName(IMAGE.FORMATS.SCALED_IMAGE.name);
            setSelectedScaleMethod(IMAGE.METHODS.ASPECT_RATIO_FULL_WIDTH);
            setDimension(widthOrHeight);
            const nextSettings = {
                format: IMAGE.FORMATS.SCALED_IMAGE.value,
                options: {
                    method: IMAGE.METHODS.ASPECT_RATIO_FULL_WIDTH,
                    dimension: widthOrHeight,
                    position: null,
                    useCaption,
                }
            };
            setSettings(nextSettings);
            onChangeImageWithSettings(image, nextSettings);
        }
    };
    const updateImageSettings = (nextSettings) => {
        setSettings(nextSettings);
        onChangeImageWithSettings(image, nextSettings);
    };
    const onImageChange = (_image) => {
        // TODO: This is resetting settings to defaults ? should it keep them
        Logger.info("[ImageInPageEditorV2] onImageChange", _image);
        if (image?.guid === _image?.guid) {
            setSettings(IMAGE.DEFAULTS.DEFAULT_IMAGE_SETTINGS);
            setImage(_image);
            onChangeImageWithSettings(
                _image,
                IMAGE.DEFAULTS.DEFAULT_IMAGE_SETTINGS
            );
        } else {
            setSettings(IMAGE.DEFAULTS.DEFAULT_IMAGE_SETTINGS);
            setImage(_image);
            onChangeImageWithSettings(
                _image,
                IMAGE.DEFAULTS.DEFAULT_IMAGE_SETTINGS
            );
        }
        setImage(_image);
    };
    const onReload = (image) => {
        console.log("onReload",image);
    };
    const onImageRemove = () => {
        setSettings(IMAGE.DEFAULTS.DEFAULT_IMAGE_SETTINGS);
        setImage(undefined);
        onRemoveImage();
        clear();
    };
    const selectScaleMethod = (event, method) => {
        let nextDimension;
        const isHeightPriority = selectedScaleMethod === IMAGE.METHODS.HEIGHT_PRIORITY;
        if (method === IMAGE.METHODS.ASPECT_RATIO_FULL_WIDTH) {
            nextDimension = 0;
        } else {
            nextDimension = isHeightPriority ? defaultWidth : defaultHeight;
        }
        setSelectedScaleMethod(method);
        const format = settings?.format;
        const useCaption = settings?.options?.useCaption;
        setDimension(widthOrHeight);
        const nextSettings = {
            format,
            options: {
                method,                      // Constants: Cropped or Scaled
                dimension: nextDimension,    // Number: width or height or user defined
                position: null,              // Constants: direction positions
                useCaption,                  // Boolean: show caption
            }
        };
        setSettings(nextSettings);
        onChangeImageWithSettings(image,nextSettings);
    };
    const selectDimension = (value) => {
        setDimension(value);
        const format = settings?.format;
        const useCaption = settings?.options?.useCaption;
        const nextSettings = {
            format,
            options: {
                method: selectedScaleMethod,
                dimension: value,
                position: IMAGE.DEFAULTS.DEFAULT_POSITION,
                useCaption,
            }
        };
        setSettings(nextSettings);
        onChangeImageWithSettings(image,nextSettings);
    };

    const onImageDeleted = async () => {}; // disabled here for now

    return (
        <div>
            <div className="in-page-container">
                <div className="in-page-container-inner">
                    <div className="in-page-image-selector">
                        <div className="simplicity-image-editor">
                            <div className="simplicity-image-editor-view">
                                <div className="image-display">
                                    <ImageSelector
                                        image={image}
                                        setImage={setImage}
                                        onSelectImage={onImageChange}
                                        onSaveImage={onImageChange}
                                        onImageRemoved={onImageRemove}
                                        onImageDeleted={onImageDeleted}
                                        debug={enableImageDebug}
                                        onReload={onReload}
                                        AddButtonComponent={AddButtonComponent}
                                        DisplayComponent={DisplayComponent}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {isFalse(imageLoading) && exists(image) &&
                        <div className="in-page-style-text-container">
                            <span className="in-page-style-text-label">Style: </span>
                            <span className="in-page-style-text">{formatName} </span>
                            {isTrue(showScaled) &&
                                <>
                                    {/*<span className="in-page-style-text-label">Style: </span>*/}
                                    <span className="in-page-style-text">/</span>
                                    <span className="in-page-style-text">{selectedScaleMethod} </span>
                                </>
                            }
                            {isTrue(showPosition) && exists(selectedPosition) &&
                                <>
                                    {/*<span className="in-page-style-text-label">Style: </span>*/}
                                    <span className="in-page-style-text">/</span>
                                    <span className="in-page-style-text">{selectedPosition} </span>
                                </>
                            }
                        </div>
                    }
                </div>
                <div className="in-page-settings-container">
                    {isFalse(imageLoading) && exists(image) &&

                        <>
                            <SelectImageFormat
                                onChange={onChooseFormat}
                                selectedFormat={selectedFormat}
                                FORMATS={IMAGE.FORMATS}
                                disableScale={disableScale}
                            />

                            {isTrue(showPosition) &&
                                <FocusAreaPosition
                                    positions={IMAGE.POSITIONS}
                                    showPosition={showPosition}
                                    selectedPosition={selectedPosition}
                                    selectPositionType={selectPositionType}
                                />
                            }

                            {isTrue(showScaled) && !disableScale &&
                                <>
                                    <ScaleMethod
                                        selectScaleMethod={selectScaleMethod}
                                        methods={IMAGE.METHOD_OPTIONS}
                                        selectedScaleMethod={selectedScaleMethod}
                                        setSelectedScaleMethod={setSelectedScaleMethod}
                                    />

                                    {selectedScaleMethod !== "100Aspect" &&
                                        <SetImageDimension
                                            dimension={dimension}
                                            selectDimension={selectDimension}
                                            whichDimension={whichDimension}
                                            width={width}
                                            height={height}
                                        />
                                    }
                                </>
                            }
                        </>
                    }
                </div>
                {enableImageDebug &&
                    <div style={{
                        width: "350px", height: "300px", overflow: "scroll",
                        background: "#dfdfdf", padding: "8px"
                    }}
                    >
                        <pre className="in-page-debug">settings: {JSON.stringify(settings, null, 2)}</pre>
                    </div>
                }

            </div>

            <ToggleUseCaption
                updateImageSettings={updateImageSettings}
                settings={settings}
            />
            {enableImageDebug &&
                <>
                    Dimensions:
                    <pre style={{
                        fontSize: "9px",
                        width: "800px",
                        overflowX: "auto",
                        backgroundColor: "#efefef"
                    }}
                    >
                        {dimension}
                    </pre>
                    <pre className="in-page-debug">
                        settings: {JSON.stringify(settings, null, 2)}
                    </pre>
                    <pre className="in-page-debug">
                        image: {JSON.stringify(image, null, 2)}
                    </pre>
                    {/*<pre className='in-page-debug'>*/}
                    {/*    image: {JSON.stringify(image, null, 2)}*/}
                    {/*</pre>*/}
                </>
            }

            {isTrue(imageLoading) &&
                <div className="loading-view">
                    <Typography variant="h3">Updating</Typography>
                    <div></div>
                </div>
            }
        </div>
    );
};

export { ImageInPageEditorV2 };
